import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wrench, Package, ShieldAlert, ListChecks, ExternalLink } from "lucide-react";
import { api } from "../lib/api.js";

/**
 * SomFloatingShortcut — fixed-position pill in the bottom-right corner that
 * provides one-click navigation back to the parent Obra de Manutenção (SOM)
 * plus sibling quick-jumps (PCPs, PECs, SWCs, Tasks).
 *
 * Visible only when a parent SOM is present. Hover expands to show the SOM
 * customer/status and sibling counts.
 *
 * Props:
 *  - caseId: SOM id. Required.
 *  - currentModule: one of 'pec' | 'pcp' | 'swc' | null — used to dim the chip
 *                   for the module the user is already in.
 */
export default function SomFloatingShortcut({ caseId, currentModule }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!caseId) { setData(null); return; }
    let cancelled = false;
    api.get(`/service-cases/${caseId}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [caseId]);

  if (!data) return null;

  const pcps = data.linked_pcps || [];
  const pecs = data.linked_pecs || [];
  const swcs = data.linked_swcs || [];

  return (
    <div
      className="fixed z-40 bottom-4 right-4 sm:bottom-6 sm:right-6"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid="som-floating-shortcut"
    >
      {/* Expanded card */}
      {open && (
        <div
          className="mb-2 bg-white border border-gray-200 rounded-md shadow-lg p-3 w-72 text-xs"
          data-testid="som-floating-shortcut-expanded"
        >
          <div className="text-[9px] uppercase tracking-widest text-[#6C757D] font-semibold mb-0.5">Obra de Manutenção</div>
          <div className="font-mono text-[#1F3864] font-bold text-sm">{data.number}</div>
          <div className="text-[#1A1A2E] truncate" title={data.customer_name}>{data.customer_name || "—"}</div>
          <div className="font-mono text-[10px] text-[#6C757D]">{data.vehicle_registration || "—"} · {data.brand || "—"}{data.model ? ` ${data.model}` : ""}</div>
          <div className="mt-1.5">
            <span className="inline-block text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[#1F3864]/10 text-[#1F3864] border border-[#1F3864]/20">
              {data.current_status || "—"}
            </span>
          </div>

          <div className="mt-3 grid grid-cols-3 gap-1.5">
            <SiblingChip
              icon={<Wrench className="w-3 h-3" />}
              label="PCPs"
              count={pcps.length}
              dim={currentModule === "pcp"}
              to={pcps[0] ? `/classification-requests?focus=${pcps[0].id}&from=som` : "/classification-requests"}
              disabled={pcps.length === 0}
              testid="som-floating-shortcut-pcp"
            />
            <SiblingChip
              icon={<Package className="w-3 h-3" />}
              label="PECs"
              count={pecs.length}
              dim={currentModule === "pec"}
              to={pecs[0] ? `/parts-requests/${pecs[0].id}` : "/parts-requests"}
              state={pecs[0] ? { from: "som" } : undefined}
              disabled={pecs.length === 0}
              testid="som-floating-shortcut-pec"
            />
            <SiblingChip
              icon={<ShieldAlert className="w-3 h-3" />}
              label="Garantias"
              count={swcs.length}
              dim={currentModule === "swc"}
              to={swcs[0] ? `/warranty-claims/${swcs[0].id}` : "/warranty-claims"}
              disabled={swcs.length === 0}
              testid="som-floating-shortcut-swc"
            />
          </div>

          <Link
            to={`/service-cases/${data.id}`}
            className="mt-3 flex items-center justify-center gap-1 w-full bg-[#1F3864] hover:bg-[#142542] text-white text-xs font-semibold rounded-sm px-3 py-1.5"
            data-testid="som-floating-shortcut-open-som"
          >
            Abrir Obra <ExternalLink className="w-3 h-3" />
          </Link>

          {data.tasks_count > 0 && (
            <div className="mt-2 text-[10px] text-[#6C757D] flex items-center gap-1">
              <ListChecks className="w-3 h-3" /> {data.tasks_count} tarefa{data.tasks_count === 1 ? "" : "s"} associada{data.tasks_count === 1 ? "" : "s"}
            </div>
          )}
        </div>
      )}

      {/* Collapsed pill — click to navigate to the SOM; hover (desktop) reveals sibling chips */}
      <Link
        to={`/service-cases/${data.id}`}
        className="inline-flex items-center gap-2 bg-[#1F3864] hover:bg-[#142542] text-white rounded-full pl-2 pr-3 py-2 shadow-lg text-xs font-semibold transition-colors"
        data-testid="som-floating-shortcut-pill"
        title={`Abrir ${data.number}`}
        aria-label={`Abrir obra ${data.number}`}
      >
        <span className="w-6 h-6 rounded-full bg-white/15 inline-flex items-center justify-center">
          <ExternalLink className="w-3 h-3" />
        </span>
        <span className="font-mono">{data.number}</span>
      </Link>
    </div>
  );
}

function SiblingChip({ icon, label, count, dim, to, state, disabled, testid }) {
  const base = "flex flex-col items-center justify-center gap-0.5 rounded-sm border px-1.5 py-1.5 transition-colors";
  if (disabled) {
    return (
      <div className={`${base} bg-[#F4F6F8] border-gray-200 text-[#9CA3AF]`} data-testid={`${testid}-disabled`}>
        <div className="flex items-center gap-0.5">{icon}<span className="font-bold">{count}</span></div>
        <div className="text-[9px] uppercase tracking-wider">{label}</div>
      </div>
    );
  }
  const cls = dim
    ? `${base} bg-[#F4F6F8] border-[#1F3864]/30 text-[#1F3864]`
    : `${base} bg-white border-gray-300 text-[#1F3864] hover:border-[#1F3864] hover:bg-[#F8FAFC]`;
  return (
    <Link to={to} state={state} className={cls} data-testid={testid}>
      <div className="flex items-center gap-0.5">{icon}<span className="font-bold">{count}</span></div>
      <div className="text-[9px] uppercase tracking-wider">{label}</div>
    </Link>
  );
}
